'use client';

import { useState, useEffect } from 'react';
import { Slider } from '../molecular/Slider';
import { Button } from '../atomic/Button';
import { Sun, Droplets, Leaf, Zap } from 'lucide-react';

interface ScenarioCalculatorProps {
  currentPotential: number;
  energySources: Array<{
    name: string;
    potentialEuro: number;
  }>;
  onScenarioCalculate: (scenario: {
    increasedSolar: number;
    increasedBiogas: number;
    increasedBiomass: number;
    increasedHydro: number;
    einspeisungRate: number;
  }) => void;
  onReset: () => void;
  compact?: boolean;
}

export function ScenarioCalculator({
  currentPotential,
  energySources,
  onScenarioCalculate,
  onReset,
  compact = false,
}: ScenarioCalculatorProps) {
  // Helper function to get 2025 growth rates from localStorage
  const get2025GrowthRates = () => {
    try {
      const keys = Object.keys(localStorage);
      const chartCacheKey = keys.find(key => key.startsWith('projection_2025_'));
      
      if (chartCacheKey) {
        const cachedStr = localStorage.getItem(chartCacheKey);
        if (cachedStr) {
          const cached = JSON.parse(cachedStr);
          return cached.growthRates || { solar: 8, water: 0.5, biomass: 1, biogas: 1 };
        }
      }
    } catch (e) {
      console.warn('[ScenarioCalculator] Could not read growth rates from cache');
    }
    
    // Fallback default growth rates
    return { solar: 8, water: 0.5, biomass: 1, biogas: 1 };
  };

  // Get initial values from 2025 projections
  const initialGrowthRates = get2025GrowthRates();
  
  const [solarIncrease, setSolarIncrease] = useState(Math.round(initialGrowthRates.solar));
  const [biogasIncrease, setBiogasIncrease] = useState(Math.round(initialGrowthRates.biogas));
  const [biomassIncrease, setBiomassIncrease] = useState(Math.round(initialGrowthRates.biomass));
  const [hydroIncrease, setHydroIncrease] = useState(Math.round(initialGrowthRates.water));
  const [einspeisungRate, setEinspeisungRate] = useState(40); // Default 40%, max 70%
  
  // Initialize sliders once when component mounts and cache becomes available
  useEffect(() => {
    let hasUpdated = false;
    
    const updateFromCache = () => {
      if (hasUpdated) return; // Only update once
      
      const growthRates = get2025GrowthRates();
      // Only update if we actually found cached data (not fallback values)
      try {
        const keys = Object.keys(localStorage);
        const chartCacheKey = keys.find(key => key.startsWith('projection_2025_'));
        
        if (chartCacheKey) {
          setSolarIncrease(Math.round(growthRates.solar));
          setBiogasIncrease(Math.round(growthRates.biogas));
          setBiomassIncrease(Math.round(growthRates.biomass));
          setHydroIncrease(Math.round(growthRates.water));
          hasUpdated = true;
          clearInterval(interval);
        }
      } catch (e) {
        // Continue checking if cache not available yet
      }
    };
    
    // Check a few times for cache, then stop
    const interval = setInterval(updateFromCache, 500);
    updateFromCache(); // Initial check
    
    // Stop checking after 5 seconds regardless
    setTimeout(() => {
      clearInterval(interval);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const calculateNewPotential = () => {
    // Get the baseline growth rates to check if values have changed from projected defaults
    const baselineRates = get2025GrowthRates();
    const isAtBaseline = (
      einspeisungRate === 40 && 
      solarIncrease === Math.round(baselineRates.solar) && 
      biogasIncrease === Math.round(baselineRates.biogas) && 
      biomassIncrease === Math.round(baselineRates.biomass) && 
      hydroIncrease === Math.round(baselineRates.water)
    );
    
    // If at projected baseline values, return current potential exactly
    if (isAtBaseline) {
      return currentPotential;
    }
    
    // Current potentials are calculated with 40% Einspeisung rate
    // Adjust for new Einspeisung rate and energy source increases
    const einspeisungMultiplier = einspeisungRate / 40; // Factor compared to current 40%
    
    const solarPotential = energySources.find(s => s.name === 'SOLAR')?.potentialEuro || 0;
    const biogasPotential = energySources.find(s => s.name === 'BIOGAS')?.potentialEuro || 0;
    const biomassPotential = energySources.find(s => s.name === 'BIOMASSE')?.potentialEuro || 0;
    const hydroPotential = energySources.find(s => s.name === 'WASSERKRAFT')?.potentialEuro || 0;

    // Calculate new base potential with adjusted Einspeisung rate
    const adjustedBasePotential = currentPotential * einspeisungMultiplier;
    
    // Base potential for new installations (when current is 0)
    // These are realistic potentials for typical new installations
    const baseBiogasPotential = 25000; // €25k for a new biogas plant
    const baseBiomassPotential = 20000; // €20k for a new biomass plant
    
    // Calculate increases from additional installations
    const solarAdjustment = (solarPotential * einspeisungMultiplier * solarIncrease) / 100;
    const hydroAdjustment = (hydroPotential * einspeisungMultiplier * hydroIncrease) / 100;
    
    // For Biogas/Biomasse: use existing potential if available, otherwise use base potential ONLY if increase > 0
    const biogasAdjustment = biogasPotential > 0 
      ? (biogasPotential * einspeisungMultiplier * biogasIncrease) / 100
      : biogasIncrease > 0 ? (baseBiogasPotential * einspeisungMultiplier * biogasIncrease) / 100 : 0;
      
    const biomassAdjustment = biomassPotential > 0
      ? (biomassPotential * einspeisungMultiplier * biomassIncrease) / 100  
      : biomassIncrease > 0 ? (baseBiomassPotential * einspeisungMultiplier * biomassIncrease) / 100 : 0;

    const totalIncrease = solarAdjustment + biogasAdjustment + biomassAdjustment + hydroAdjustment;

    return Math.round(adjustedBasePotential + totalIncrease);
  };

  const handleCalculate = () => {
    onScenarioCalculate({
      increasedSolar: solarIncrease,
      increasedBiogas: biogasIncrease,
      increasedBiomass: biomassIncrease,
      increasedHydro: hydroIncrease,
      einspeisungRate: einspeisungRate,
    });
  };

  const handleReset = () => {
    // Reset to 2025 projected growth rates instead of 0
    const growthRates = get2025GrowthRates();
    setSolarIncrease(Math.round(growthRates.solar));
    setBiogasIncrease(Math.round(growthRates.biogas));
    setBiomassIncrease(Math.round(growthRates.biomass));
    setHydroIncrease(Math.round(growthRates.water));
    setEinspeisungRate(40);
    onReset();
  };

  const newPotential = calculateNewPotential();
  const increase = newPotential - currentPotential;
  const increasePercentage = Math.round((increase / currentPotential) * 100);

  return (
    <div className={`${compact ? '' : 'p-4 bg-white dark:bg-gray-800 rounded-lg shadow'}`}>
      {!compact && <h3 className="text-lg font-semibold mb-4">Szenario erstellen</h3>}
      
      <div className={compact ? 'space-y-2' : 'space-y-4'}>
        <div className={compact ? 'space-y-1.5' : 'space-y-3'}>
          <Slider
            label="Einspeisung"
            icon={Zap}
            iconColor="text-green-500"
            value={einspeisungRate}
            onChange={setEinspeisungRate}
            min={40}
            max={70}
            step={5}
            unit="%"
            compact={compact}
            tooltip="Anteil der Energie, der in der Energiegemeinschaft geteilt werden kann (Standard: 40%)"
          />
          
          <Slider
            label="Solar"
            icon={Sun}
            iconColor="text-yellow-500"
            value={solarIncrease}
            onChange={setSolarIncrease}
            min={0}
            max={200}
            step={5}
            unit="%"
            compact={compact}
            tooltip="Simulieren Sie zusätzliche PV-Anlagen auf Dächern und Freiflächen"
          />
          
          <Slider
            label="Biogas"
            icon={Leaf}
            iconColor="text-pink-500"
            value={biogasIncrease}
            onChange={setBiogasIncrease}
            min={0}
            max={200}
            step={5}
            unit="%"
            compact={compact}
            tooltip="Potenzial durch neue Biogasanlagen aus landwirtschaftlichen Reststoffen"
          />
          
          <Slider
            label="Biomasse"
            icon={Leaf}
            iconColor="text-purple-500"
            value={biomassIncrease}
            onChange={setBiomassIncrease}
            min={0}
            max={200}
            step={5}
            unit="%"
            compact={compact}
            tooltip="Zusätzliche Biomasse-Kraftwerke mit Holz aus nachhaltiger Forstwirtschaft"
          />
          
          <Slider
            label="Wasserkraft"
            icon={Droplets}
            iconColor="text-blue-500"
            value={hydroIncrease}
            onChange={setHydroIncrease}
            min={0}
            max={200}
            step={5}
            unit="%"
            compact={compact}
            tooltip="Neue Kleinwasserkraftwerke (unter 10 MW) an geeigneten Standorten"
          />
        </div>

        <div className={`${compact ? 'pt-1' : 'pt-3 border-t border-gray-200 dark:border-gray-700'}`}>
          <div className="flex justify-between items-center mb-2">
            <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700 dark:text-gray-300`}>
              {compact ? 'Neues Potenzial' : 'Neues Potenzial:'}
            </span>
            <div className="text-right">
              <div className={`${compact ? 'text-base' : 'text-lg'} font-bold text-green-600 dark:text-green-500`}>
                €{newPotential.toLocaleString('de-AT')}/Jahr
              </div>
              {increase > 0 && (
                <div className={`${compact ? 'text-xs' : 'text-sm'} text-green-500 dark:text-green-400`}>
                  +{increasePercentage}% (€{increase.toLocaleString('de-AT')}/Jahr)
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button 
              onClick={handleReset}
              variant="outline"
              className={compact ? 'text-xs px-2 py-1' : 'px-4 py-2'}
            >
              {compact ? 'Zurück' : 'Zurücksetzen'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
